import { useState, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import {
    Plus,
    Trash,
    Pencil,
    DotsSixVertical,
    CaretUp,
    CaretDown,
    Copy,
    TextT,
    TextAlignLeft,
    Hash,
    Calendar,
    ListBullets,
    CheckSquare,
    EnvelopeSimple,
    Phone,
    Eye,
    EyeSlash,
    CaretRight
} from '@phosphor-icons/react';

// Field type definitions
const FIELD_TYPES = [
    { value: 'text', label: 'Testo', icon: TextT },
    { value: 'textarea', label: 'Area di Testo', icon: TextAlignLeft },
    { value: 'number', label: 'Numero', icon: Hash },
    { value: 'date', label: 'Data', icon: Calendar },
    { value: 'select', label: 'Selezione', icon: ListBullets },
    { value: 'radio', label: 'Radio', icon: CheckSquare },
    { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
    { value: 'email', label: 'Email', icon: EnvelopeSimple },
    { value: 'phone', label: 'Telefono', icon: Phone },
    { value: 'hidden', label: 'Nascosto', icon: EyeSlash },
    { value: 'readonly', label: 'Solo Lettura', icon: Eye },
];

const FIELD_WIDTHS = [
    { value: 'full', label: 'Intera riga' },
    { value: 'half', label: 'Metà riga' },
    { value: 'third', label: 'Un terzo' },
];

// Generate unique ID
const generateId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const generateSectionId = () => `section_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ==================== FIELD EDITOR DIALOG ====================
function FieldEditorDialog({ open, onOpenChange, field, onSave, isNew = false }) {
    const [formData, setFormData] = useState(field || {
        id: generateId(),
        name: '',
        label: '',
        type: 'text',
        required: false,
        placeholder: '',
        default_value: '',
        options: [],
        validations: {},
        width: 'full',
        order: 0,
        help_text: ''
    });
    const [newOption, setNewOption] = useState({ value: '', label: '' });

    const handleSave = () => {
        if (!formData.name || !formData.label) return;
        
        // Generate name from label if not set
        const name = formData.name || formData.label.toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_]/g, '');
        
        onSave({ ...formData, name });
        onOpenChange(false);
    };

    const addOption = () => {
        if (newOption.value && newOption.label) {
            setFormData(prev => ({
                ...prev,
                options: [...prev.options, { ...newOption }]
            }));
            setNewOption({ value: '', label: '' });
        }
    };

    const removeOption = (index) => {
        setFormData(prev => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index)
        }));
    };

    const needsOptions = ['select', 'radio'].includes(formData.type);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isNew ? 'Nuovo Campo' : 'Modifica Campo'}</DialogTitle>
                    <DialogDescription>
                        Configura le proprietà del campo form
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Etichetta *</Label>
                            <Input
                                value={formData.label}
                                onChange={(e) => setFormData(prev => ({ 
                                    ...prev, 
                                    label: e.target.value,
                                    name: prev.name || e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
                                }))}
                                placeholder="Es: Nome Cliente"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Nome Campo (ID)</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                                placeholder="nome_cliente"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Tipo Campo</Label>
                            <Select
                                value={formData.type}
                                onValueChange={(v) => setFormData(prev => ({ ...prev, type: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {FIELD_TYPES.map(t => (
                                        <SelectItem key={t.value} value={t.value}>
                                            <div className="flex items-center gap-2">
                                                <t.icon className="w-4 h-4" />
                                                {t.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Larghezza</Label>
                            <Select
                                value={formData.width}
                                onValueChange={(v) => setFormData(prev => ({ ...prev, width: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {FIELD_WIDTHS.map(w => (
                                        <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Placeholder</Label>
                        <Input
                            value={formData.placeholder || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, placeholder: e.target.value }))}
                            placeholder="Testo visualizzato quando vuoto"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Valore Default</Label>
                        <Input
                            value={formData.default_value || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, default_value: e.target.value }))}
                            placeholder="Valore precompilato"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Testo di Aiuto</Label>
                        <Input
                            value={formData.help_text || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, help_text: e.target.value }))}
                            placeholder="Suggerimento per l'utente"
                        />
                    </div>

                    {/* Options for select/radio */}
                    {needsOptions && (
                        <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                            <Label>Opzioni</Label>
                            <div className="flex gap-2">
                                <Input
                                    value={newOption.value}
                                    onChange={(e) => setNewOption(prev => ({ ...prev, value: e.target.value }))}
                                    placeholder="Valore"
                                    className="w-1/3"
                                />
                                <Input
                                    value={newOption.label}
                                    onChange={(e) => setNewOption(prev => ({ ...prev, label: e.target.value }))}
                                    placeholder="Etichetta"
                                    className="flex-1"
                                />
                                <Button type="button" variant="outline" onClick={addOption}>
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                            {formData.options.length > 0 && (
                                <div className="space-y-1">
                                    {formData.options.map((opt, idx) => (
                                        <div key={`opt-${opt.value}-${idx}`} className="flex items-center gap-2 bg-white p-2 rounded border">
                                            <span className="text-xs font-mono text-muted-foreground w-24 truncate">{opt.value}</span>
                                            <CaretRight className="w-3 h-3 text-muted-foreground" />
                                            <span className="flex-1 text-sm">{opt.label}</span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removeOption(idx)}
                                                className="h-6 w-6 p-0 text-destructive"
                                            >
                                                <Trash className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Validations */}
                    <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                        <Label>Validazioni</Label>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="required"
                                    checked={formData.required}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, required: checked }))}
                                />
                                <Label htmlFor="required" className="text-sm font-normal">Campo Obbligatorio</Label>
                            </div>
                        </div>
                        {(formData.type === 'text' || formData.type === 'textarea') && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <Label className="text-xs">Min Caratteri</Label>
                                    <Input
                                        type="number"
                                        value={formData.validations?.minLength || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            validations: { ...prev.validations, minLength: e.target.value ? parseInt(e.target.value) : undefined }
                                        }))}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">Max Caratteri</Label>
                                    <Input
                                        type="number"
                                        value={formData.validations?.maxLength || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            validations: { ...prev.validations, maxLength: e.target.value ? parseInt(e.target.value) : undefined }
                                        }))}
                                    />
                                </div>
                            </div>
                        )}
                        {formData.type === 'number' && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <Label className="text-xs">Valore Minimo</Label>
                                    <Input
                                        type="number"
                                        value={formData.validations?.min || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            validations: { ...prev.validations, min: e.target.value ? parseFloat(e.target.value) : undefined }
                                        }))}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs">Valore Massimo</Label>
                                    <Input
                                        type="number"
                                        value={formData.validations?.max || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            validations: { ...prev.validations, max: e.target.value ? parseFloat(e.target.value) : undefined }
                                        }))}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
                    <Button onClick={handleSave} disabled={!formData.label}>
                        {isNew ? 'Aggiungi Campo' : 'Salva Modifiche'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ==================== SECTION EDITOR DIALOG ====================
function SectionEditorDialog({ open, onOpenChange, section, onSave, isNew = false }) {
    const [formData, setFormData] = useState(section || {
        id: generateSectionId(),
        code: '',
        title: '',
        description: '',
        order: 0,
        collapsible: false,
        collapsed_default: false,
        fields: []
    });

    const handleSave = () => {
        if (!formData.title) return;
        const code = formData.code || formData.title.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
        onSave({ ...formData, code });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{isNew ? 'Nuova Sezione' : 'Modifica Sezione'}</DialogTitle>
                    <DialogDescription>
                        Configura le proprietà della sezione form
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label>Titolo Sezione *</Label>
                        <Input
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                title: e.target.value,
                                code: prev.code || e.target.value.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '')
                            }))}
                            placeholder="Es: Dati Cliente"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Codice Sezione</Label>
                        <Input
                            value={formData.code}
                            onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s+/g, '_') }))}
                            placeholder="DATI_CLIENTE"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Descrizione</Label>
                        <Textarea
                            value={formData.description || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Descrizione opzionale della sezione"
                            rows={2}
                        />
                    </div>

                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="collapsible"
                                checked={formData.collapsible}
                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, collapsible: checked }))}
                            />
                            <Label htmlFor="collapsible" className="text-sm font-normal">Sezione Collassabile</Label>
                        </div>
                        {formData.collapsible && (
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="collapsed_default"
                                    checked={formData.collapsed_default}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, collapsed_default: checked }))}
                                />
                                <Label htmlFor="collapsed_default" className="text-sm font-normal">Chiusa di Default</Label>
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
                    <Button onClick={handleSave} disabled={!formData.title}>
                        {isNew ? 'Crea Sezione' : 'Salva Modifiche'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ==================== MAIN FORM BUILDER COMPONENT ====================
export default function StepFormBuilder({ sections = [], onChange, readOnly = false }) {
    const [showSectionDialog, setShowSectionDialog] = useState(false);
    const [showFieldDialog, setShowFieldDialog] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const sortedSections = useMemo(() => [...sections].sort((a, b) => a.order - b.order), [sections]);

    // Section handlers
    const addSection = (sectionData) => {
        const newSection = {
            ...sectionData,
            order: sections.length,
            fields: sectionData.fields || []
        };
        onChange([...sections, newSection]);
    };

    const updateSection = (sectionData) => {
        onChange(sections.map(s => s.id === sectionData.id ? sectionData : s));
    };

    const deleteSection = (sectionId) => {
        onChange(sections.filter(s => s.id !== sectionId));
    };

    const moveSection = (sectionId, direction) => {
        const index = sections.findIndex(s => s.id === sectionId);
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === sections.length - 1)) return;
        
        const newSections = [...sections];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
        
        // Update order
        newSections.forEach((s, i) => s.order = i);
        onChange(newSections);
    };

    // Field handlers
    const addField = (sectionId, fieldData) => {
        onChange(sections.map(s => {
            if (s.id === sectionId) {
                const newField = {
                    ...fieldData,
                    order: s.fields.length
                };
                return { ...s, fields: [...s.fields, newField] };
            }
            return s;
        }));
    };

    const updateField = (sectionId, fieldData) => {
        onChange(sections.map(s => {
            if (s.id === sectionId) {
                return {
                    ...s,
                    fields: s.fields.map(f => f.id === fieldData.id ? fieldData : f)
                };
            }
            return s;
        }));
    };

    const deleteField = (sectionId, fieldId) => {
        onChange(sections.map(s => {
            if (s.id === sectionId) {
                return {
                    ...s,
                    fields: s.fields.filter(f => f.id !== fieldId)
                };
            }
            return s;
        }));
    };

    const moveField = (sectionId, fieldId, direction) => {
        onChange(sections.map(s => {
            if (s.id === sectionId) {
                const fields = [...s.fields];
                const index = fields.findIndex(f => f.id === fieldId);
                if ((direction === 'up' && index === 0) || (direction === 'down' && index === fields.length - 1)) return s;
                
                const newIndex = direction === 'up' ? index - 1 : index + 1;
                [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
                fields.forEach((f, i) => f.order = i);
                
                return { ...s, fields };
            }
            return s;
        }));
    };

    const duplicateField = (sectionId, field) => {
        const newField = {
            ...field,
            id: generateId(),
            name: `${field.name}_copy`,
            label: `${field.label} (Copia)`
        };
        addField(sectionId, newField);
    };

    const getFieldTypeIcon = (type) => {
        const fieldType = FIELD_TYPES.find(t => t.value === type);
        return fieldType?.icon || TextT;
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h4 className="font-medium">Configurazione Form</h4>
                    <p className="text-sm text-muted-foreground">
                        {sections.length} sezioni, {sections.reduce((acc, s) => acc + s.fields.length, 0)} campi totali
                    </p>
                </div>
                {!readOnly && (
                    <Button 
                        onClick={() => { setEditingSection(null); setShowSectionDialog(true); }}
                        size="sm"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Nuova Sezione
                    </Button>
                )}
            </div>

            {/* Sections */}
            {sections.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
                    <TextAlignLeft className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-3">Nessuna sezione configurata</p>
                    {!readOnly && (
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setEditingSection(null); setShowSectionDialog(true); }}
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            Aggiungi Prima Sezione
                        </Button>
                    )}
                </div>
            ) : (
                <Accordion 
                    type="multiple" 
                    defaultValue={sections.map(s => s.id)}
                    className="space-y-2"
                >
                    {sortedSections.map((section, sectionIndex) => (
                        <AccordionItem 
                            key={section.id} 
                            value={section.id}
                            className="border rounded-lg overflow-hidden bg-white"
                        >
                            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30">
                                <div className="flex items-center gap-3 flex-1">
                                    <div className="flex flex-col gap-0.5">
                                        {!readOnly && (
                                            <>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); moveSection(section.id, 'up'); }}
                                                    className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                                                    disabled={sectionIndex === 0}
                                                >
                                                    <CaretUp className="w-3 h-3" />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); moveSection(section.id, 'down'); }}
                                                    className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                                                    disabled={sectionIndex === sections.length - 1}
                                                >
                                                    <CaretDown className="w-3 h-3" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex-1 text-left">
                                        <span className="font-medium">{section.title}</span>
                                        <span className="text-xs text-muted-foreground ml-2">
                                            ({section.fields.length} campi)
                                        </span>
                                    </div>
                                    <code className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground">
                                        {section.code}
                                    </code>
                                    {!readOnly && (
                                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0"
                                                onClick={() => { setEditingSection(section); setShowSectionDialog(true); }}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                                onClick={() => deleteSection(section.id)}
                                            >
                                                <Trash className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                                {section.description && (
                                    <p className="text-sm text-muted-foreground mb-3">{section.description}</p>
                                )}
                                
                                {/* Fields */}
                                <div className="space-y-2">
                                    {[...section.fields].sort((a, b) => a.order - b.order).map((field, fieldIndex) => {
                                        const FieldIcon = getFieldTypeIcon(field.type);
                                        return (
                                            <div 
                                                key={field.id}
                                                className="flex items-center gap-2 p-2 bg-muted/30 rounded-md group"
                                            >
                                                {!readOnly && (
                                                    <div className="flex flex-col gap-0.5 opacity-50 group-hover:opacity-100">
                                                        <button
                                                            onClick={() => moveField(section.id, field.id, 'up')}
                                                            className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                                                            disabled={fieldIndex === 0}
                                                        >
                                                            <CaretUp className="w-3 h-3" />
                                                        </button>
                                                        <button
                                                            onClick={() => moveField(section.id, field.id, 'down')}
                                                            className="p-0.5 hover:bg-muted rounded disabled:opacity-30"
                                                            disabled={fieldIndex === section.fields.length - 1}
                                                        >
                                                            <CaretDown className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                )}
                                                <FieldIcon className="w-4 h-4 text-muted-foreground" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-medium truncate">{field.label}</span>
                                                        {field.required && (
                                                            <Badge variant="destructive" className="text-[10px] h-4 px-1">Obbligatorio</Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                        <span className="font-mono">{field.name}</span>
                                                        <span>•</span>
                                                        <span>{FIELD_TYPES.find(t => t.value === field.type)?.label || field.type}</span>
                                                        <span>•</span>
                                                        <span>{FIELD_WIDTHS.find(w => w.value === field.width)?.label || 'Intera riga'}</span>
                                                    </div>
                                                </div>
                                                {!readOnly && (
                                                    <div className="flex gap-1 opacity-50 group-hover:opacity-100">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 w-7 p-0"
                                                            onClick={() => { 
                                                                setActiveSectionId(section.id);
                                                                setEditingField(field); 
                                                                setShowFieldDialog(true); 
                                                            }}
                                                        >
                                                            <Pencil className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 w-7 p-0"
                                                            onClick={() => duplicateField(section.id, field)}
                                                        >
                                                            <Copy className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                                            onClick={() => deleteField(section.id, field.id)}
                                                        >
                                                            <Trash className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Add Field Button */}
                                {!readOnly && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full mt-3 border-dashed"
                                        onClick={() => {
                                            setActiveSectionId(section.id);
                                            setEditingField(null);
                                            setShowFieldDialog(true);
                                        }}
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Aggiungi Campo
                                    </Button>
                                )}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            )}

            {/* Section Dialog */}
            <SectionEditorDialog
                open={showSectionDialog}
                onOpenChange={setShowSectionDialog}
                section={editingSection}
                onSave={editingSection ? updateSection : addSection}
                isNew={!editingSection}
            />

            {/* Field Dialog */}
            <FieldEditorDialog
                open={showFieldDialog}
                onOpenChange={setShowFieldDialog}
                field={editingField}
                onSave={(fieldData) => {
                    if (editingField) {
                        updateField(activeSectionId, fieldData);
                    } else {
                        addField(activeSectionId, fieldData);
                    }
                }}
                isNew={!editingField}
            />
        </div>
    );
}

// Export field types for use in other components
export { FIELD_TYPES, FIELD_WIDTHS };
